/**
 * Random Character Service
 * ------------------------
 * Produces a randomized character state. Respects gender restrictions on
 * jobs and the current server filter (when the availability cache is warm).
 *
 * Does NOT touch: action, headdir, cash mount toggle.
 * The action row's Reset button acts as the "undo" — no confirmation prompt.
 */

import { getCachedAvailableIds } from "@/services/divinePrideService";

// ────────────────────────────────────────────────────────────────────────────
// Static pools
// ────────────────────────────────────────────────────────────────────────────

// Extracted from JobList.vue — every job the simulator can render (via the
// primary backend or the iRO Wiki fallback). Jobs that render on neither backend
// stay out of this pool (still manually selectable, just not randomly rolled).
const ALL_JOBS = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 16, 17, 18, 19, 20, 22, 23,
  24, 25, 26, 27, 28, 29, 30,
  4008, 4009, 4010, 4011, 4012, 4013, 4015, 4016, 4017, 4018, 4019, 4020, 4021,
  4046, 4047, 4049, 4060, 4061, 4062, 4063, 4064, 4065, 4066, 4067, 4068, 4069,
  4070, 4071, 4072, 4190, 4211, 4212, 4215, 4218, 4239, 4240,
  4252, 4253, 4254, 4255, 4256, 4257, 4258, 4259, 4260, 4261, 4262, 4263, 4264,
  // Expanded 4th classes (render via the iRO Wiki fallback backend):
  4302, 4303, 4304, 4305, 4306, 4307, 4308,
];

// From JobList.vue isGenderSpecificJob(). Gender convention: male = 1, female = 0.
// Includes the Bard/Dancer 4th evolutions (Troubadour 4263 / Trouvere 4264) and
// the Kagerou/Oboro 4th evolutions (Shinkiro 4304 / Shiranui 4305).
const MALE_ONLY_JOBS = new Set([19, 4020, 4068, 4211, 4263, 4304]);
const FEMALE_ONLY_JOBS = new Set([20, 4021, 4069, 4212, 4264, 4305]);

// From HeadList.vue:83 — the Doram race has its own hair sprite set.
// 4308 is excluded from ALL_JOBS above but still listed here in case a Doram
// 4th-class job is ever added back into the random pool.
const DORAM_JOBS = new Set([4218, 4308]);

// Hair style ranges (HeadList.vue:58-61).
const HUMAN_HAIR_COUNT = 42; // 1..42 (styles 32-42 render via the iRO Wiki backend)
const DORAM_HAIR_COUNT = 10; // 1..10

// Hair palette: store's `headPalette` is 0-indexed (SAVE_HAIR_COLOR does id-1).
// HairColorList renders 15 swatches (01..15) → headPalette 0..14.
const HAIR_PALETTE_COUNT = 15;

// Body palette: 0=regular, 1=blue, 2=red, 3=green (BodyPallete.vue).
const BODY_PALETTE_COUNT = 4;

// Empty-slot probabilities (see spec).
const EMPTY_CHANCE = {
  top: 0.3,
  mid: 0.6,
  bot: 0.5,
  garment: 0.4,
};

// Chance of the outfit (alt costume) being enabled.
const OUTFIT_CHANCE = 0.2;

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const randInt = (max) => Math.floor(Math.random() * max); // [0, max)
const randChance = (p) => Math.random() < p;

/**
 * Pick a uniformly random element from an array, or null if empty.
 */
const pickOne = (arr) => {
  if (!arr || arr.length === 0) return null;
  return arr[randInt(arr.length)];
};

/**
 * Filter items by server availability using the cached pool.
 *
 * Policy: if the server filter is on AND we have at least one cached match,
 * intersect with the cached set. If the cache is cold (no intersection yet),
 * fall back to the unfiltered pool — otherwise randomize would be broken on
 * first use before the user has browsed any tabs.
 */
const applyServerFilter = (items, serverFilter) => {
  if (serverFilter !== "LATAM") return items;
  const availableIds = getCachedAvailableIds(serverFilter);
  if (availableIds.size === 0) return items; // cold cache, skip filter
  const filtered = items.filter((it) => availableIds.has(parseInt(it.id, 10)));
  return filtered.length > 0 ? filtered : items; // no matches, degrade gracefully
};

// ────────────────────────────────────────────────────────────────────────────
// Gender / job selection
// ────────────────────────────────────────────────────────────────────────────

/**
 * Pick a random gender (0 or 1) and a compatible job in one shot.
 */
const rollGenderAndJob = () => {
  const gender = randInt(2); // 0 or 1
  const forbidden = gender === 1 ? FEMALE_ONLY_JOBS : MALE_ONLY_JOBS;
  const pool = ALL_JOBS.filter((id) => !forbidden.has(id));
  const job = pickOne(pool);
  return { gender, job: String(job) };
};

/**
 * Pick a hair style ID appropriate for the job + gender combination.
 * Doram jobs get 1..10, everyone else gets 1..31.
 */
const rollHead = (jobNumeric) => {
  const count = DORAM_JOBS.has(jobNumeric) ? DORAM_HAIR_COUNT : HUMAN_HAIR_COUNT;
  return randInt(count) + 1;
};

// ────────────────────────────────────────────────────────────────────────────
// Headgear / garment selection
// ────────────────────────────────────────────────────────────────────────────

/**
 * Roll a single equipment slot. Returns { viewID, item } where item is the
 * full DB record (for hydration into headgear_*_item store slots), or null
 * if the slot should stay empty.
 */
const rollSlot = (items, emptyChance, serverFilter) => {
  if (randChance(emptyChance)) return null;
  const pool = applyServerFilter(items, serverFilter);
  const item = pickOne(pool);
  if (!item) return null;
  return {
    viewID: parseInt(item.viewID, 10) || 0,
    item,
  };
};

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

/**
 * Produce a randomized character payload ready to commit to the Vuex store.
 *
 * @param {Object} ctx
 * @param {Array}  ctx.itemDb         - The current item DB (state.ui.jsonFile)
 * @param {string} ctx.serverFilter   - 'LATAM' or 'all'
 * @param {number} [ctx.cashMount]    - Pass-through for cash mount toggle (preserved)
 * @returns {{
 *   character: Object,
 *   topItem: Object|{},
 *   midItem: Object|{},
 *   bottomItem: Object|{},
 *   garmentItem: Object|{}
 * }}
 */
export const buildRandomCharacter = ({ itemDb, serverFilter }) => {
  const { gender, job } = rollGenderAndJob();
  const jobNumeric = parseInt(job, 10);

  // Pre-filter the DB by slot category + visible viewID (mirrors store getters).
  const tops = itemDb.filter(
    (it) => it.top === true && parseInt(it.viewID, 10) > 0
  );
  const mids = itemDb.filter(
    (it) => it.mid === true && parseInt(it.viewID, 10) > 0
  );
  const bots = itemDb.filter(
    (it) => it.bot === true && parseInt(it.viewID, 10) > 0
  );
  const garments = itemDb.filter(
    (it) => it.garment === true && parseInt(it.viewID, 10) > 0
  );

  const topRoll = rollSlot(tops, EMPTY_CHANCE.top, serverFilter);
  const midRoll = rollSlot(mids, EMPTY_CHANCE.mid, serverFilter);
  const botRoll = rollSlot(bots, EMPTY_CHANCE.bot, serverFilter);
  const garmentRoll = rollSlot(garments, EMPTY_CHANCE.garment, serverFilter);

  // Store shape: headgear is [bottom, top, mid] — see store mutations.
  const headgear = [
    botRoll?.viewID ?? 0,
    topRoll?.viewID ?? 0,
    midRoll?.viewID ?? 0,
  ];

  return {
    character: {
      gender,
      job: [job], // stored as array per store convention
      head: rollHead(jobNumeric),
      headPalette: randInt(HAIR_PALETTE_COUNT),
      headdir: 0, // always neutral — avoids confusing sprite orientation
      headgear,
      garment: garmentRoll?.viewID ?? 0,
      bodyPalette: randInt(BODY_PALETTE_COUNT),
      action: 0, // always idle
      canvas: "200x200+100+150",
      outfit: randChance(OUTFIT_CHANCE) ? 1 : 0,
    },
    topItem: topRoll?.item ?? {},
    midItem: midRoll?.item ?? {},
    bottomItem: botRoll?.item ?? {},
    garmentItem: garmentRoll?.item ?? {},
  };
};
